import { useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import {
  getCurrentSnapshot,
  clearSnapshotAccessChecks,
  authorizePinnedSnapshot,
  getSnapshotAccessVisit,
  type ApiRequestOptions,
  type SnapshotCurrentResponse,
} from "./api";
import { isDemoPointerAvailabilityFailure, isTransientPointerError, queryRetryDelay } from "./queryRetry";

/**
 * Roots are deliberately module-memory only: browser storage must never hold a
 * root or snapshot payload. A pin lasts until an explicit session invalidation.
 */
const pins = new Map<string, SnapshotCurrentResponse>();
const pending = new Map<string, Promise<SnapshotCurrentResponse>>();
const pendingVisits = new Map<string, string>();
const controllers = new Map<string, AbortController>();
let generation = 0;
const listeners = new Set<() => void>();
const CHANNEL = "alta-session-invalidation";

function notify() {
  generation += 1;
  listeners.forEach((listener) => listener());
}

function clearLocalPins() {
  clearSnapshotAccessChecks();
  controllers.forEach((controller) => controller.abort());
  controllers.clear();
  pins.clear();
  pending.clear();
  pendingVisits.clear();
  notify();
}

const channel = typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel(CHANNEL)
  : null;
channel?.addEventListener("message", (event: MessageEvent<{ type?: string }>) => {
  if (event.data?.type === "clear-snapshot-pins") clearLocalPins();
});

/** Clears every root pin and tells other open tabs to fail closed as well. */
export function clearSnapshotPins(broadcast = true) {
  clearLocalPins();
  if (broadcast) channel?.postMessage({ type: "clear-snapshot-pins" });
}

/** Forces the selected herd to resolve a root again without weakening auth. */
export function invalidateSnapshotPin(herdId: string) {
  controllers.get(herdId)?.abort();
  controllers.delete(herdId);
  pins.delete(herdId);
  pending.delete(herdId);
  pendingVisits.delete(herdId);
  notify();
}

export async function resolvePinnedSnapshot(
  herdId: string,
  requestOptions?: ApiRequestOptions,
  freshVisit = false,
): Promise<SnapshotCurrentResponse> {
  const existing = pins.get(herdId);
  if (existing && !freshVisit) {
    // A pin preserves source identity, not authorization across page visits.
    await authorizePinnedSnapshot(existing.root_hash, [herdId]);
    return existing;
  }
  const visit = getSnapshotAccessVisit();
  const inFlight = pending.get(herdId);
  if (inFlight && pendingVisits.get(herdId) === visit) return inFlight;
  // A fast SPA navigation must not attach the new page to an old visit's
  // soon-to-be-cancelled pointer request.
  controllers.get(herdId)?.abort();
  const controller = new AbortController();
  controllers.set(herdId, controller);
  let request!: Promise<SnapshotCurrentResponse>;
  request = getCurrentSnapshot(herdId, {
    ...requestOptions,
    signal: controller.signal,
  }, freshVisit && !!existing)
    .then((pointer) => {
      // A session invalidation while the request was outstanding must not
      // reintroduce a root from the old identity.
      if (pending.get(herdId) === request) pins.set(herdId, pointer);
      return pointer;
    })
    .catch((error: unknown) => {
      // Rejections (including React Query's AbortSignal cancellation) can
      // never remain reusable. Only remove our own promise: a new session or
      // request may already have installed another one for this herd.
      if (pending.get(herdId) === request) {
        pending.delete(herdId);
        pendingVisits.delete(herdId);
      }
      throw error;
    })
    .finally(() => {
      if (pending.get(herdId) === request) {
        pending.delete(herdId);
        pendingVisits.delete(herdId);
      }
      if (controllers.get(herdId) === controller) controllers.delete(herdId);
    });
  pending.set(herdId, request);
  pendingVisits.set(herdId, visit);
  return request;
}

/** Test-only observable reset; it never persists pins or snapshot payloads. */
export function resetSnapshotPinsForTest() {
  clearSnapshotPins(false);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function getGeneration() { return generation; }

/** Resolves once per selected herd/session; never auto-replaces a pinned root. */
export function usePinnedSnapshot(
  herdId: string | null | undefined,
  identityReady = true,
  /** Demo sessions use the mutable, intentionally non-canonical live surface. */
  enabled = true,
) {
  const location = useLocation();
  const pinGeneration = useSyncExternalStore(subscribe, getGeneration, getGeneration);
  const visit = getSnapshotAccessVisit();
  const query = useQuery({
    queryKey: ["snapshot-current", herdId, pinGeneration, location.key, visit],
    // Do not couple this one-shot pointer read to React Query's mount signal.
    // Session invalidation clears `pending` and increments the query generation,
    // so the identity guard in resolvePinnedSnapshot already prevents any late
    // response from reintroducing an old identity's root.
    queryFn: () => resolvePinnedSnapshot(herdId!, undefined, true),
    enabled: enabled && !!herdId && identityReady,
    staleTime: Infinity,
    gcTime: 0,
    // A root remains pinned once resolved, but a short backend-start window
    // must not permanently strand the page in an error state. Retry only
    // transport/gateway/database-availability failures; validation, auth and
    // privacy failures remain fail-closed.
    retry: (failureCount, error) => failureCount < 6 && isTransientPointerError(error),
    retryDelay: queryRetryDelay,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // Lightweight online access/status only. Never change the selected root or
  // data query keys when a poll starts or another release becomes available.
  const status = useQuery({
    queryKey: ["snapshot-access-status", herdId, query.data?.root_hash, location.key, visit],
    queryFn: () => authorizePinnedSnapshot(query.data!.root_hash, [herdId!]),
    enabled: enabled && identityReady && !!herdId && !!query.data && !query.isError,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (count, error) => count < 2 && isTransientPointerError(error),
    retryDelay: queryRetryDelay,
    meta: { quietStatus: true },
  });
  const newDataAvailable = !!status.data && !!query.data && (
    status.data.root_hash !== query.data.root_hash ||
    status.data.overlay_bundle_revision?.overlay_hash !== query.data.overlay_bundle_revision?.overlay_hash
  );
  // A transient demo status/pool failure is not a revocation. Keep the already
  // authorized demo pin visible; explicit rights/privacy failures still close
  // the gate. Do not change the real-herd security lifecycle in this fix.
  const statusDenied = status.isError && !(herdId === "demo-mustermann"
    && isDemoPointerAvailabilityFailure(status.error));
  // A failed pointer transport must not strand the demo. Its legacy endpoints
  // independently authorize the request and also serve an installed edition,
  // so this does not bypass a rights/privacy rejection or expose real farms.
  const legacyDemo = herdId === "demo-mustermann" && !query.data?.demo_frozen
    && (!!query.data || (query.isError && isDemoPointerAvailabilityFailure(query.error)));
  // Never render cached customer bodies while the visit's online gate is
  // rejected. The initial pointer itself proves access. A successful background
  // check remains valid while the next check is in flight; no empty-key query.
  return { ...query, newDataAvailable, legacyDemo, refresh: query.refetch,
    isError: (query.isError && !legacyDemo) || statusDenied,
    error: query.error ?? (statusDenied ? status.error : null),
    data: enabled && identityReady && !query.isError && !statusDenied
      ? query.data : undefined };
}